import { useRef, useMemo, useState, useEffect } from 'react'
import { Canvas as RfCanvas, useFrame, useThree } from '@react-three/fiber'
import { useSpring, animated, config } from '@react-spring/three'
import { animated as a, useSpring as s, useTransition } from '@react-spring/web'
import { Html, Stats } from '@react-three/drei'
import { Color } from 'three'
import { NoiseEffect } from 'components/noiseEffect'
import { getPointInSphere } from 'hooks/useGetPointInSphere'
import { LabelGizmo } from './labelGizmo'
import { useStore } from 'hooks/useStore'
import { size } from '../breakpoints'
import styled, { useTheme } from 'styled-components'
import { Trans } from 'react-i18next'

// TODO: https://codesandbox.io/s/q4d2v
// Particles

{
  /* TODO: Size on the bubble group instead */
}
const Bubble = (props: any) => {
  const shaderMaterialRef = useRef<any>()
  const { camera } = useThree()
  const mesh = useRef<any>()

  // position tweening
  // const { position, scale } = useSpring({
  //   position: props.position,
  //   scale: props.scale,
  //   config: config.slow,
  // })

  // opacity tweening
  useSpring({
    opacity: props.opacity,
    config: { duration: 3000 },
    onChange: ({ value }) => {
      // Take care of the plane opacity
      shaderMaterialRef.current.uniforms.alpha.value = value.opacity
    },
  })

  const fragmentShader = `
      uniform vec3 color;
      uniform float alpha;
      varying vec2 vUv;

      float drawGradientArc(vec2 center, vec2 currentPosition, float scale) {
          float dist = distance(center, currentPosition) * (2.0 / scale);
          return 1.0 - dist;
      }

      void main() {
          vec2 center = vec2(0.5, 0.5);
          float alpha = drawGradientArc(center, vUv, alpha);
          gl_FragColor = vec4(color, alpha);
      }`

  const vertexShader = `
      varying vec2 vUv;

      void main() {
          vUv = uv;
          vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
          gl_Position = projectionMatrix * mvPosition;
      }`

  const uniforms = useMemo(
    () => ({
      color: {
        type: 'c',
        value: new Color(
          props.colors[Math.floor(Math.random() * props.colors.length)]
        ),
      },
      alpha: { type: 'f', value: props.opacity },
    }),
    []
  )

  const rd = useMemo(() => [Math.random(), Math.random(), Math.random()], [])

  useFrame(({ clock }) => {
    // Take care of the plane position: we want a random movement
    // const pos = [
    //   position.animation.values[0]?.lastPosition || props.position[0],
    //   position.animation.values[1]?.lastPosition || props.position[1],
    //   position.animation.values[2]?.lastPosition || props.position[2],
    // ]

    const pos = [props.position[0], props.position[1], props.position[2]]

    if (mesh && mesh.current) {
      mesh.current.position.x =
        pos[0] +
        (-1 + rd[0] * 2 + Math.sin(clock.getElapsedTime() * 1)) *
          (-1 + props.speed * 2) *
          props.noise *
          (-1 + rd[0] * 2)
      mesh.current.position.y =
        pos[1] +
        (-1 + rd[0] * 2 + Math.sin(clock.getElapsedTime() * 1)) *
          (-1 + props.speed * 2) *
          props.noise *
          (-1 + rd[1] * 2)
      mesh.current.position.z =
        pos[2] +
        (-1 + rd[0] * 2 + Math.sin(clock.getElapsedTime() * 1)) *
          (-1 + props.speed * 2) *
          props.noise *
          (-1 + rd[2] * 2)

      // Orient the plane towards the camera
      mesh.current.lookAt(camera.position)
    }
  })

  // Return view, these are regular three.js elements expressed in JSX
  return (
    // <animated.mesh {...props} position={position} scale={scale} ref={mesh}>
    <animated.mesh
      {...props}
      position={props.position}
      scale={props.scale}
      ref={mesh}
    >
      <planeGeometry args={[1, 1, 1, 1]} />
      <shaderMaterial
        ref={shaderMaterialRef}
        transparent
        depthWrite={false}
        attach='material'
        uniforms={uniforms}
        fragmentShader={fragmentShader}
        vertexShader={vertexShader}
      />
    </animated.mesh>
  )
}

{
  /* 
    TODO: Size on group, not on the particles
    TODO: Dynamic density based on the size of the group
*/
}
const BubbleGroup = ({
  position,
  radius,
  density,
  bubbleSize,
  label,
  showCircle,
  showLabel,
  labelPositionLeft,
  labelPosition,
  noise,
  opacity,
  dynamicScaling,
  colors,
}: {
  position: any
  // [[rangeA, rangeB], [radiusA, radiusB]]
  radius: [[number, number], [number, number]]
  density: any
  bubbleSize: any
  label?: any
  showCircle?: boolean
  showLabel?: boolean
  labelPositionLeft?: boolean
  labelPosition?: string,
  noise: any
  opacity?: number
  dynamicScaling?: boolean
  colors: string[]
}) => {
  // const initialDensity = Math.min(density * radius[1][0], 120)
  // const [d, setD] = useState(initialDensity)
  const group = useRef<any>()
  const bubbles = useMemo(
    () =>
      Array.from({ length: 200 }, (v, i) => ({
        seeds: [Math.random(), Math.random(), Math.random()],
        id: i,
        props: {
          speed: Math.random(),
          scale: Math.random(),
          noise: noise,
          colors: colors,
        },
      })),
    []
  )
  const [visibleBubbles, setVisibleBubbles] = useState(bubbles.slice(0, density))
  const [ss, aa] = s(() => ({
    pct: 0,
    radius: radius[1][0],
    config: { mass: 5, tension: 400, friction: 50 },
  }))

  // Transient updates (for often occurring state-changes)
  // Connect to the store on mount, disconnect on unmount, catch state-changes in a reference
  useEffect(() => {
    useStore.subscribe(({ percentage }) => {
      const radPct = Math.min(
        Math.max(
          0,
          (percentage - radius[0][0]) / (radius[0][1] - radius[0][0])
        ),
        1
      )
      const rad = radius[1][0] + radPct * (radius[1][1] - radius[1][0])
      // setD(Math.min(density * rad, 120))
      aa.start({
        pct: radPct,
        radius: rad,
      })
    })
  }, [radius, density])

//   useEffect(() => {
//     setVisibleBubbles(bubbles.slice(0, d))
//  }, [d])

  useFrame(() => {
    if (group && group.current) {
      group.current.rotateY(0.005)
    }
  })

  const AnimatedBubble = animated(Bubble)

  return (
    <group position={position}>
      {/* @ts-ignore */}
      {label && <animated.mesh position={ss.radius.to((v: any) => [
          v * (labelPositionLeft ? -1 : 1),
          0,
          0,
        ])}
      >
        <Html>
          <>
            {/* @ts-ignore */}
            <LabelGizmo
              position={labelPosition}
              label={label}
              show={showLabel}
            />
          </>
        </Html>
      </animated.mesh>}
      
      {!showCircle && (
        <group ref={group}>
          {visibleBubbles.map((bubble) => (
            // @ts-ignore
            <AnimatedBubble
              {...bubble.props}
              key={`bubble-${bubble.id}`}
              opacity={opacity}
              scale={
                dynamicScaling
                  ? ss.radius.to((v: any) => Math.min(v * 1, 4))
                  : (1 + Math.random() * 2) * bubbleSize
              }
              // Position of the bubble element in the bubble group
              position={ss.radius.to((v: any) => getPointInSphere(
                  v,
                  bubble.seeds[0],
                  bubble.seeds[1],
                  bubble.seeds[2]
                )
              )}
            />
          ))}
        </group>
      )}
    </group>
  )
}

const CameraControls = () => {
  const isMobile = window.innerWidth < size.tablet

  // The camera path. Each position is a [start, end] percentage of the total path
  // and the corresponding camera position
  const positions: [[number, number], [number[], number[]]][] = [
    [
      [0, 0.1],
      [
        isMobile ? [-2, -4, 5] : [0, -1.5, 4],
        [0, -4, 8],
      ],
    ],
    [
      [0.1, 0.25],
      [
        [0, -4, 8],
        [0, -4, 8],
      ],
    ],
    [
      [0.25, 0.32],
      [
        [0, -4, 8],
        [0, -4, 5],
      ],
    ],
    [
      [0.32, 0.5],
      [
        [0, -4, 5],
        [0, -4, 5],
      ],
    ],
    [
      [0.5, 0.75],
      [
        [0, -4, 5],
        [15, -4, 15],
      ],
    ],
    [
      [0.75, 1],
      [
        [15, -4, 15],
        [7.5, -4, 20],
      ],
    ],
  ]
  const { camera } = useThree()
  const [spring, api] = useSpring(() => ({
    position: positions[0][1][0],
    config: config.slow,
    onChange: (res) => {
      camera.position.x = res.value.position[0]
      camera.position.y = res.value.position[1]
      camera.position.z = res.value.position[2]

      camera.lookAt(camera.position.x, camera.position.y, camera.position.z)
    },
  }))

  // Transient updates (for often occurring state-changes)
  // Connect to the store on mount, disconnect on unmount, catch state-changes in a reference
  useEffect(() => {
    // Rotate the camera on mobile -> from wide to portrait
    isMobile && camera.up.set(0, 0, 1)
    // Wee need a lookAt to make sure the camera is oriented correctly
    camera.lookAt(camera.position.x, camera.position.y, camera.position.z)

    camera.position.x = positions[0][1][0][0]
    camera.position.y = positions[0][1][0][1]
    camera.position.z = positions[0][1][0][2]

    useStore.subscribe(({ percentage }) => {
      // Find the position in the path that corresponds to the current percentage
      const values = positions.find(([range, position]) => {
        if (percentage >= range[0] && percentage <= range[1]) {
          return [range, position]
        }
      }) || [
        [0, 0],
        [
          [0, 0, 0],
          [0, 0, 0],
        ],
      ]

      // Calculate the position based on the percentage
      const pct = (percentage - values[0][0]) / (values[0][1] - values[0][0])
      const pos = [
        values[1][0][0] + (values[1][1][0] - values[1][0][0]) * pct,
        values[1][0][1] + (values[1][1][1] - values[1][0][1]) * pct,
        values[1][0][2] + (values[1][1][2] - values[1][0][2]) * pct,
      ]

      api.start({
        position: pos,
      })
    })
  }, [])

  return null
}

export const Canvas = () => {
  const isMobile = window.innerWidth < size.tablet
  const [stage, setStage] = useState(0)
  const theme = useTheme()
  const bubbleColors =
    theme.colorMode == 'dark'
      ? ['#666666', '#777777', '#787878']
      : ['#BBBBBB', '#CCCCCC', '#CDCDCD']

  useEffect(
    () =>
      useStore.subscribe(({ stage }) => {
        setStage(stage)
      }),
    []
  )

  const swissRadius = stage === 2 ? 10 : 3
  const endRadiusHighIncome = stage === 4 ? 10 : 7.9

  return (
    <CanvasContainer>
      <RfCanvas>
        {process.env.NODE_ENV !== 'production' && <Stats />}
        <NoiseEffect />
        <CameraControls />

        {/* Suisse -> explose au stage 2 */}
        <BubbleGroup
          colors={bubbleColors}
          position={[0, -4, 0]}
          radius={[
            [0, 1],
            [swissRadius, swissRadius],
          ]}
          density={100}
          bubbleSize={0.85}
          noise={1}
          label={<Trans i18nKey='intro:canvasLabels:cloud1' />}
          opacity={stage >= 2 ? 0 : 1}
          dynamicScaling={false}
          showLabel={stage === 1}
          labelPosition={isMobile ? 'top left' : 'bottom left'}
        />

        {/* Bas revenus */}
        <BubbleGroup
          colors={bubbleColors}
          position={[0, -4, 0]}
          radius={[
            [0.25, 0.5],
            [0.025, 1.7],
          ]}
          density={50}
          bubbleSize={0.05}
          noise={1}
          opacity={[2, 3].includes(stage) ? 1 : 0}
          dynamicScaling={true}
          showLabel={stage > 2 && stage < 4}
          labelPositionLeft
          labelPosition='bottom right'
          label={<Trans i18nKey='intro:canvasLabels:cloud2' />}
        />

        {/* Haut revenus */}
        <BubbleGroup
          colors={bubbleColors}
          position={[15, -4, 0]}
          radius={[
            [0.5, 0.75],
            [0.025, endRadiusHighIncome],
          ]}
          density={100}
          bubbleSize={1}
          noise={1}
          opacity={[3].includes(stage) ? 1 : 0}
          dynamicScaling={true}
          showLabel={stage > 2 && stage < 4}
          labelPosition={isMobile ? 'top left' : 'bottom left'}
          label={<Trans i18nKey='intro:canvasLabels:cloud3' />}
        />

        {/* SUISSE - Bas revenus */}
        <BubbleGroup
          colors={bubbleColors}
          position={[isMobile ? 0 : 0, -4, 0]}
          radius={[
            [0.5, 0.75],
            [0, 5],
          ]}
          density={100}
          bubbleSize={0.05}
          noise={1}
          opacity={[4].includes(stage) ? 1 : 0}
          dynamicScaling={true}
        />

        {/* SUISSE - Haut revenus */}
        <BubbleGroup
          colors={bubbleColors}
          position={[isMobile ? 15 : 15, -4, 0]}
          radius={[
            [0.5, 0.75],
            [0, 7],
          ]}
          density={100}
          bubbleSize={1}
          noise={1}
          opacity={[4].includes(stage) ? 1 : 0}
          dynamicScaling={true}
        />

        {/* Population bas revenus */}
        <BubbleGroup
          colors={bubbleColors}
          position={[isMobile ? 4 : 0, -4, 0]}
          radius={[
            [0.66, 1],
            [4.4, 4.4],
          ]}
          density={0}
          bubbleSize={5}
          noise={0}
          label={<Trans i18nKey='intro:canvasLabels:cloud4' />}
          labelPositionLeft
          labelPosition='bottom right'
          opacity={stage >= 4 ? 1 : 0}
          dynamicScaling={false}
          showCircle={stage >= 4}
          showLabel={stage >= 4}
        />

        {/* Population hauts revenus */}
        <BubbleGroup
          colors={bubbleColors}
          position={[isMobile ? 13 : 15, -4, 0]}
          radius={[
            [0.66, 1],
            [2, 2],
          ]}
          density={0}
          bubbleSize={3}
          noise={0}
          labelPosition={isMobile ? 'top left' : 'bottom left'}
          label={<Trans i18nKey='intro:canvasLabels:cloud5' />}
          opacity={stage >= 4 ? 1 : 0}
          dynamicScaling={true}
          showCircle={stage >= 4}
          showLabel={stage >= 4}
        />
      </RfCanvas>
    </CanvasContainer>
  )
}

const CanvasContainer = styled.div`
  background-color: var(--background-color);
  width: 100%;
  height: 100vh;
`
